/**
 * WorkflowDetail — the 2-pane routine split view (P12 §6.1, T-FE3).
 *
 * The route component for /workflows/new and /workflows/:id. It is the split container:
 *   - the RoutineBuilder chat on the LEFT (the PRIMARY builder — chat-first, mirroring the
 *     dashboard-preview-in-chat model) ‖ the routine editor/preview on the RIGHT;
 *   - the two panes are RESIZABLE (drag the divider; the layout persists per user), and stack
 *     vertically on mobile (chat on top);
 *   - both panes are wrapped in {@link RoutineDraftProvider} so they SHARE one draft — agent patches
 *     flow chat → store → editor live, and the editor is human-editable once the agent is idle.
 *
 * Session keying (§4.5): the RoutineBuilder session is keyed on a STABLE id — the saved `workflowId`
 * when editing, or a client-minted `draftLocalId` (uuid) for a brand-new routine — so the session
 * survives the new→saved transition without re-keying. The provider seeds from `workflowId`; the chat
 * is keyed on `workflowId ?? draftLocalId`.
 *
 * A brand-new routine's draft lives only in memory until the first Save, so we guard against an
 * accidental reload/close wiping in-progress work with a `beforeunload` prompt (NP-A).
 */
import { RotateCcw, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/_shared/components/ui/resizable';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { RoutineChatPanel } from '../components/RoutineChatPanel';
import { RoutineHeader } from '../components/RoutineHeader';
import { WorkflowEditor } from '../components/WorkflowEditor';
import { WorkflowRepairBanner } from '../components/WorkflowRepairBanner';
import { RoutineDraftProvider, useRoutineDraftContext } from '../contexts/RoutineDraftContext';
import { useRoutineChat } from '../hooks/useRoutineChat';

export function WorkflowDetail(): React.ReactElement {
  const { id } = useParams<{ id?: string }>();
  usePageTitle(id ? 'Routine — Workflows' : 'New Routine — Workflows');

  // Stable draft-local id for a brand-new routine — minted once (lazy init), survives new→saved (§4.5).
  const [draftLocalId] = useState<string>(() => crypto.randomUUID());
  const sessionKey = id ?? draftLocalId;

  return (
    <RoutineDraftProvider workflowId={id} draftSessionKey={draftLocalId}>
      <SplitView sessionKey={sessionKey} />
    </RoutineDraftProvider>
  );
}

/** Inner view — needs the draft context (the store) to wire the chat to the shared draft. */
function SplitView({ sessionKey }: { sessionKey: string }): React.ReactElement {
  const { store, actions } = useRoutineDraftContext();
  const chat = useRoutineChat(store, sessionKey);
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();
  // A confirmed repair or a dismissed notice refreshes the routine and the list's status.
  const repairInvalidateKeys = useMemo(() => [orpc.workflow.key()], [orpc]);
  const isMobile = useIsMobile();

  // NP-A: a brand-new routine (no saved id yet) is in-memory only — warn before a reload/close throws
  // away an in-progress draft or conversation. (Only for never-saved routines; saved ones autosave.)
  const hasUnsavedNewWork = !store.workflowId && (store.saveState === 'unsaved' || chat.messages.length > 0);
  useEffect(() => {
    if (!hasUnsavedNewWork) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedNewWork]);

  // Load failed: show a full-page error state with Retry — no header/panels over a blank draft (which
  // would let the action bar fire against nothing). The right pane no longer renders its own error.
  if (store.loadError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <TriangleAlert />
            </EmptyMedia>
            <EmptyTitle>Couldn&apos;t load routine</EmptyTitle>
            <EmptyDescription>
              {store.loadError.message || 'Something went wrong loading this routine.'}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              variant="secondary"
              onClick={() => store.refetch()}
              loading={store.isRefetching}
              Icon={<RotateCcw />}
            >
              Retry
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* One page-level header spanning both panes: name + description, Run / Discard / Save, and the
          Reset-conversation control. The panes themselves carry no section headers. */}
      <RoutineHeader
        agentActive={chat.agentActive}
        onResetChat={chat.resetChat}
        canResetChat={chat.messages.length > 0}
      />
      {store.workflowId && store.accountRepair && (
        <WorkflowRepairBanner
          workflowId={store.workflowId}
          view={store.accountRepair}
          invalidateKeys={repairInvalidateKeys}
          isAdmin={isAdmin}
          onResume={actions.start}
          resuming={actions.isStarting}
        />
      )}

      <ResizablePanelGroup
        direction={isMobile ? 'vertical' : 'horizontal'}
        autoSaveId="workflow-split-layout"
        className="min-h-0 flex-1"
      >
        {/* Left/top: RoutineBuilder chat — the PRIMARY builder. */}
        <ResizablePanel id="routine-chat" defaultSize="55%" minSize="30%" className="bg-background">
          <RoutineChatPanel
            sessionId={chat.sessionId}
            messages={chat.messages}
            agentActive={chat.agentActive}
            canStop={chat.canStop}
            stopping={chat.stopping}
            stopStatusError={chat.stopStatusError}
            stopStalled={chat.stopStalled}
            onRetryStopStatus={chat.retryStopStatus}
            disabled={!chat.sessionReady}
            onSend={chat.sendMessage}
            onStop={chat.stopTurn}
            prefillValue={chat.prefillValue}
            prefillNonce={chat.prefillNonce}
          />
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Right/bottom: routine editor/preview panel (fills in live from agent patches; human-editable
            when the agent is idle). */}
        <ResizablePanel id="routine-editor" defaultSize="45%" minSize="30%">
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <WorkflowEditor agentActive={chat.agentActive} />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
